<script lang="ts">
	import { metadata } from '$lib/content/data/metadata';
	import { courses } from '$lib/content/data/courses';
	import { cohortCourses } from '$lib/content/data/cohort-courses';
	import CurrencySelector from '$lib/components/CurrencySelector.svelte';
	import FAQItem from '$lib/components/FAQItem.svelte';
	import LocalSessionTime from '$lib/components/LocalSessionTime.svelte';
	import { pricingState } from '$lib/stores/pricing.svelte';
	import { getPricing, type CurrencyCode } from '$lib/utils/pricing';
	import type { Course } from '$lib/types';

	const description =
		'Live courses from Akonia Codex — free 90-minute masterclasses and paid cohort programs in Web GIS, Python, Golang, and Agentic AI. Learn spec-driven development with an instructor.';

	let selectedCategory = $state<'free' | 'all' | Course['category']>('free');
	let selectedLevel = $state<'all' | Course['level']>('all');

	const filteredCourses = $derived(() => {
		let result = courses;

		if (selectedCategory !== 'all') {
			result = result.filter((c) => c.category === selectedCategory);
		}

		if (selectedLevel !== 'all') {
			result = result.filter((c) => c.level === selectedLevel);
		}

		const levelRank: Record<Course['level'], number> = { foundation: 0, basic: 1, advanced: 2 };
		return [...result].sort((a, b) => {
			if (a.highlighted !== b.highlighted) return a.highlighted ? -1 : 1;
			return levelRank[a.level] - levelRank[b.level];
		});
	});

	// 'free' (the live sessions) is the first tab and the default — funnel entry
	const categoryLabels: Record<'free' | 'all' | Course['category'], string> = {
		free: 'Free Sessions',
		all: 'All Courses',
		programming: 'Programming',
		'agentic-ai': 'Agentic AI'
	};

	const levelLabels: Record<Course['level'] | 'all', string> = {
		all: 'All Levels',
		foundation: 'Foundation',
		basic: 'Basic',
		advanced: 'Advanced'
	};

	const levelColors: Record<Course['level'], string> = {
		foundation: 'bg-ak-wintergreen-dim text-ak-wintergreen',
		basic: 'bg-ak-wintergreen/10 text-ak-wintergreen/80',
		advanced: 'bg-ak-wintergreen/20 text-ak-wintergreen'
	};

	// FAQ data
	const faqs = [
		{
			question: 'What programming experience do I need to start?',
			answer:
				"If you're starting from zero, Python Basic and HTML/CSS/JS Basic work great — no experience needed. For the advanced stuff, you'll want to have some coding background first."
		},
		{
			question: 'How are the courses delivered?',
			answer:
				"Live sessions with an instructor. You code along, ask questions, and build actual projects. We usually run sessions on weekends so people with jobs can still make it."
		},
		{
			question: "What's included in the course fee?",
			answer:
				"Live sessions, projects you actually build, all the materials you need, and a certificate when you're done. Plus access to our community if you get stuck later."
		},
		{
			question: 'What is the BMAD tool in the Agentic AI course?',
			answer:
				"BMAD is the tool we use to build with AI using spec-driven development. Instead of treating AI like a magic chat box, you'll learn to write clear specs that guide AI to build exactly what you need."
		},
		{
			question: 'Do you offer any discounts or payment plans?',
			answer:
				"Early-bird pricing for new batches. For courses over ₹10,000, we can do payment plans if you need that. Reach out and we'll see what works."
		},
		{
			question: 'What happens if I miss a session?',
			answer:
				"Everything's recorded and available within a day. Miss something? Watch the replay. Still stuck? Ask in the community or show up to doubt-clearing sessions."
		},
		{
			question: 'Will I get a certificate?',
			answer:
				'Yep — finish the course and projects, and you get a certificate. Put it on LinkedIn, show your boss, whatever you want.'
		},
		{
			question: 'How do I know which course is right for me?',
			answer:
				"Book a free call and we'll help you figure it out. No pressure — we just want to make sure you don't end up in the wrong level."
		},
		{
			question: 'Are these courses suitable for students in Malaysia, Singapore, India, and Kerala?',
			answer:
				'Absolutely. We schedule sessions for IST (Indian Standard Time) with recordings available across time zones. Payments accepted via UPI, bank transfer, and international cards. Our students join from Kuala Lumpur, Singapore, Mumbai, Bangalore, Kochi, Thiruvananthapuram, and Kozhikode.'
		}
	];

	// Currency change handler
	function handleCurrencyChange(_currency: CurrencyCode) {
		// Handled by global state
	}

	const pageTitle = 'Courses — Free Live Sessions & Paid Cohorts | Akonia Codex';

	// ItemList Structured Data (free sessions + paid courses).
	// Free sessions and paid courses both resolve at /courses/{slug}.
	const courseListEntries = [
		...cohortCourses.filter((c) => !c.detailRedirect).map((c) => ({ title: c.title, url: `/courses/${c.slug}` })),
		...courses.map((c) => ({ title: c.title, url: `/courses/${c.slug}` }))
	];

	const courseListSchema = {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		name: 'Akonia Codex Courses',
		itemListElement: courseListEntries.map((entry, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			url: `${metadata.company.website}${entry.url}`,
			name: entry.title
		}))
	};
</script>

<svelte:head>
	<title>{pageTitle}</title>
	<link rel="canonical" href="{metadata.company.website}/courses" />
	<meta name="description" content={description} />
	<meta property="og:title" content={pageTitle} />
	<meta property="og:description" content={description} />
	<meta property="og:type" content="website" />
	<meta property="og:url" content="{metadata.company.website}/courses" />
	<meta property="og:image" content="{metadata.company.website}/images/og/courses.png" />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta property="og:image:alt" content="Akonia Codex course catalog - Learn to build with AI" />
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={pageTitle} />
	<meta name="twitter:description" content={description} />
	<meta name="twitter:image" content="{metadata.company.website}/images/og/courses.png" />
	<meta name="twitter:image:alt" content="Akonia Codex course catalog - Learn to build with AI" />

	<!-- Structured Data -->
	{@html `<script type="application/ld+json">${JSON.stringify(courseListSchema)}</` + `script>`}

	<!-- BreadcrumbList Structured Data -->
	{@html `<script type="application/ld+json">${JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: 'Home',
				item: metadata.company.website
			},
			{
				'@type': 'ListItem',
				position: 2,
				name: 'Courses',
				item: `${metadata.company.website}/courses`
			}
		]
	})}</` + `script>`}
</svelte:head>

<main id="main-content">
	<!-- Hero -->
	<section class="relative py-12 lg:py-20 overflow-hidden border-b border-ak-border-subtle">
		<div class="absolute inset-0 bg-linear-dark"></div>
		<div class="absolute top-0 left-1/2 -translate-x-1/2 w-150 h-75 bg-linear-glow opacity-30 blur-3xl pointer-events-none"></div>

		<div class="relative container mx-auto px-4 text-center">
			<!-- Badge -->
			<div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-ak-wintergreen-dim border border-ak-border mb-5">
				<span class="w-2 h-2 rounded-full bg-ak-wintergreen animate-pulse"></span>
				<span class="text-xs font-bold text-ak-wintergreen tracking-wide">LEARN SPEC-DRIVEN DEVELOPMENT</span>
			</div>

			<h1 class="font-display font-bold mb-3">
				<span class="text-ak-text-primary">Master</span>{' '}<span class="text-gradient">Spec-Driven</span>{' '}<span class="text-ak-text-primary">AI Development</span>
			</h1>
			<p class="text-base md:text-lg lg:text-xl text-ak-text-secondary leading-relaxed max-w-2xl mx-auto mb-6">
				Start with a free 90-minute live masterclass, then go deep with paid cohort courses — Web GIS,
				Python, Golang, and Agentic AI, taught live by an instructor.
			</p>

			<!-- Quick stats -->
			<div class="grid grid-cols-3 gap-2 sm:gap-4 max-w-lg sm:max-w-xl mx-auto mb-6">
				<div class="flex flex-col items-center gap-1.5 rounded-lg border border-ak-border-subtle bg-linear-card px-2 py-4 text-center">
					<svg class="w-5 h-5 text-ak-wintergreen shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
					</svg>
					<span class="text-sm font-semibold text-ak-text-primary leading-tight">{cohortCourses.length} Free</span>
					<span class="text-xs text-ak-text-muted leading-tight">Live sessions</span>
				</div>
				<div class="flex flex-col items-center gap-1.5 rounded-lg border border-ak-border-subtle bg-linear-card px-2 py-4 text-center">
					<svg class="w-5 h-5 text-ak-wintergreen shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18c1.746 0 3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
					</svg>
					<span class="text-sm font-semibold text-ak-text-primary leading-tight">{courses.length} Paid</span>
					<span class="text-xs text-ak-text-muted leading-tight">Cohort courses</span>
				</div>
				<div class="flex flex-col items-center gap-1.5 rounded-lg border border-ak-border-subtle bg-linear-card px-2 py-4 text-center">
					<svg class="w-5 h-5 text-ak-wintergreen shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
					</svg>
					<span class="text-sm font-semibold text-ak-text-primary leading-tight">100% Live</span>
					<span class="text-xs text-ak-text-muted leading-tight">Instructor-led</span>
				</div>
			</div>

			<!-- CTA -->
			<div>
				<a
					href="#courses"
					class="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-12 bg-ak-wintergreen text-ak-deep font-semibold rounded-md hover:opacity-90 active:opacity-80 active:scale-[0.98] transition-all touch-manipulation"
				>
					Browse Free Sessions
					<svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
					</svg>
				</a>
			</div>

			<!-- Trust indicator -->
			<p class="text-ak-text-muted text-xs mt-4">
				Weekend-friendly IST schedule • Recordings with every session
			</p>
		</div>
	</section>

	<!-- Course catalog — free sessions + paid courses behind one filter bar -->
	<section id="courses" class="py-8 lg:py-12 scroll-mt-20">
		<div class="container mx-auto px-4">
			<!-- Section header with currency selector -->
			<div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
				<div>
					<h2 class="font-display font-bold">Course catalog</h2>
					<p class="text-ak-text-secondary text-sm mt-1">Real skills in 90 minutes free, or go deep with a paid cohort</p>
				</div>
				{#if selectedCategory !== 'free'}
					<CurrencySelector onCurrencyChange={handleCurrencyChange} />
				{/if}
			</div>

			<!-- Filter bar -->
			<div class="flex flex-col sm:flex-row gap-3 mb-6">
				<!-- Category filter -->
				<div class="flex flex-wrap gap-2">
					{#each Object.entries(categoryLabels) as [value, label]}
						<button
							onclick={() => (selectedCategory = value as 'free' | 'all' | Course['category'])}
							aria-pressed={selectedCategory === value}
							class="px-3.5 py-2 text-sm font-medium rounded-md transition-all min-h-11 touch-manipulation {selectedCategory === value
								? 'bg-ak-wintergreen text-ak-deep'
								: 'bg-ak-surface border border-ak-border-subtle text-ak-text-secondary hover:border-ak-wintergreen/50'}"
						>
							{label}
						</button>
					{/each}
				</div>

				<!-- Level filter (paid courses only) -->
				{#if selectedCategory !== 'free'}
					<div class="flex flex-wrap gap-2 sm:ml-auto">
						{#each Object.entries(levelLabels) as [value, label]}
							<button
								onclick={() => (selectedLevel = value as 'all' | Course['level'])}
								aria-pressed={selectedLevel === value}
								class="px-3.5 py-2 text-sm font-medium rounded-md transition-all min-h-11 touch-manipulation {selectedLevel === value
									? 'bg-ak-wintergreen text-ak-deep'
									: 'bg-ak-surface border border-ak-border-subtle text-ak-text-secondary hover:border-ak-wintergreen/50'}"
							>
								{label}
							</button>
						{/each}
					</div>
				{/if}
			</div>

			<!-- Course grid -->
			{#if selectedCategory !== 'free' && selectedCategory !== 'all' && filteredCourses().length === 0}
				<div class="text-center py-12">
					<p class="text-ak-text-secondary">No courses match your filters.</p>
					<button
						onclick={() => {
							selectedCategory = 'all';
							selectedLevel = 'all';
						}}
						class="mt-3 text-ak-wintergreen hover:underline"
					>
						Clear filters
					</button>
				</div>
			{:else}
				<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					<!-- Free live sessions (pinned first on Free + All tabs) -->
					{#if selectedCategory === 'free' || selectedCategory === 'all'}
						{#each cohortCourses as course (course.slug)}
							<a
								href={course.detailRedirect ?? `/courses/${course.slug}`}
								class="group relative flex flex-col bg-linear-card rounded-lg border border-ak-wintergreen/30 overflow-hidden hover:border-ak-wintergreen/60 transition-colors active:opacity-95 touch-manipulation"
							>
								<div class="absolute top-3 right-3 z-10">
									<span class="px-2.5 py-0.5 text-xs font-bold bg-ak-wintergreen text-ak-deep rounded-full">
										FREE
									</span>
								</div>

								<div class="p-4 sm:p-5 pb-4">
									<div class="flex items-start justify-between mb-3">
										<div class="w-10 h-10 rounded-lg bg-ak-wintergreen-dim flex items-center justify-center group-hover:bg-ak-wintergreen/20 transition-colors">
											<svg class="w-5 h-5 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
											</svg>
										</div>
									</div>

									<h3 class="font-display text-lg font-bold mb-1.5 group-hover:text-ak-wintergreen transition-colors">
										{course.title}
									</h3>

									<p class="text-ak-text-secondary text-sm leading-relaxed line-clamp-2">
										{course.description}
									</p>
								</div>

								<!-- Learn preview -->
								<div class="px-4 sm:px-5 pb-4 grow">
									<ul class="space-y-1.5">
										{#each course.learn.slice(0, 3) as feature}
											<li class="flex items-start gap-2 text-sm text-ak-text-secondary">
												<svg class="w-4 h-4 text-ak-wintergreen mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
													<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
												</svg>
												<span class="line-clamp-1">{feature}</span>
											</li>
										{/each}
										{#if course.learn.length > 3}
											<li class="text-sm text-ak-text-secondary pl-6">
												+{course.learn.length - 3} more
											</li>
										{/if}
									</ul>
								</div>

								<!-- Card footer -->
								<div class="px-4 sm:px-5 py-3.5 border-t border-ak-border-subtle bg-ak-deep/30">
									<div class="flex items-center justify-between">
										<span class="flex items-center gap-1.5 text-sm text-ak-text-secondary">
											<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
											</svg>
											Live <LocalSessionTime cohort={course.cohort} variant="date" /> · {course.cohort.sessionLength}
										</span>
										<span class="font-display font-bold text-ak-wintergreen">Free</span>
									</div>
								</div>
							</a>
						{/each}
					{/if}

					<!-- Paid courses -->
					{#if selectedCategory !== 'free'}
						{#each filteredCourses() as course (course.id)}
						<a
							href="/courses/{course.slug}"
							class="group relative flex flex-col bg-linear-card rounded-lg border border-ak-border-subtle overflow-hidden hover:border-ak-wintergreen/50 transition-colors active:opacity-95 touch-manipulation"
						>
							<!-- Featured badge -->
							{#if course.highlighted}
								<div class="absolute top-3 right-3 z-10">
									<span class="px-2.5 py-0.5 text-xs font-bold bg-ak-wintergreen text-ak-deep rounded-full">
										FEATURED
									</span>
								</div>
							{/if}

							<!-- Card header with category icon -->
							<div class="p-4 sm:p-5 pb-4">
								<div class="flex items-start justify-between mb-3">
									<div class="w-10 h-10 rounded-lg bg-ak-wintergreen-dim flex items-center justify-center group-hover:bg-ak-wintergreen/20 transition-colors">
										{#if course.category === 'agentic-ai'}
											<svg class="w-5 h-5 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
											</svg>
										{:else}
											<svg class="w-5 h-5 text-ak-wintergreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
											</svg>
										{/if}
									</div>
									<span class="px-2.5 py-0.5 text-xs font-medium rounded-full {levelColors[course.level]}">
										{levelLabels[course.level]}
									</span>
								</div>

								<h3 class="font-display text-lg font-bold mb-1.5 group-hover:text-ak-wintergreen transition-colors">
									{course.title}
								</h3>

								<p class="text-ak-text-secondary text-sm leading-relaxed line-clamp-2">
									{course.description}
								</p>
							</div>

							<!-- Features preview -->
							<div class="px-4 sm:px-5 pb-4 grow">
								<ul class="space-y-1.5">
									{#each course.features.slice(0, 3) as feature}
										<li class="flex items-start gap-2 text-sm text-ak-text-secondary">
											<svg class="w-4 h-4 text-ak-wintergreen mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
											</svg>
											<span class="line-clamp-1">{feature}</span>
										</li>
									{/each}
									{#if course.features.length > 3}
										<li class="text-sm text-ak-text-secondary pl-6">
											+{course.features.length - 3} more
										</li>
									{/if}
								</ul>
							</div>

							<!-- Card footer -->
							<div class="px-4 sm:px-5 py-3.5 border-t border-ak-border-subtle bg-ak-deep/30">
								<div class="flex items-center justify-between">
									<div class="flex items-center gap-4 text-sm text-ak-text-secondary">
										<span class="flex items-center gap-1">
											<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
											</svg>
											{course.duration}
										</span>
									</div>
									<div class="font-display font-bold text-ak-wintergreen">
										{getPricing(course.price, pricingState.selectedCurrency)}
									</div>
								</div>
							</div>
						</a>
					{/each}
					{/if}
				</div>
			{/if}
		</div>
	</section>

	<!-- FAQ -->
	<section class="py-8 lg:py-12 bg-ak-surface/30 border-t border-ak-border-subtle content-visibility-auto">
		<div class="container mx-auto px-4">
			<div class="max-w-3xl mx-auto">
				<h2 class="font-display font-bold mb-1">Frequently asked questions</h2>
				<p class="text-sm text-ak-text-muted mb-4">Everything you need to know about our courses</p>

				<div class="space-y-3">
					{#each faqs as faq, index}
						<FAQItem question={faq.question} answer={faq.answer} itemIndex={index} />
					{/each}
				</div>
			</div>
		</div>
	</section>
</main>

<style>
	.line-clamp-3 {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 3;
		line-clamp: 3;
		overflow: hidden;
	}
	.line-clamp-2 {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		overflow: hidden;
	}
	.line-clamp-1 {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 1;
		line-clamp: 1;
		overflow: hidden;
	}
</style>
